import { rd, RemoteData } from '@passionware/monads';
import { Home, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { appRouting } from 'v5/core/routing/appRouting';
import { Button } from 'v5/design-system/Button';
import { EnterpriseSidebarEmptyState } from 'v5/features/core/enterprise/EnterpriseSidebarEmptyState';
import { EnterpriseSidebarIdentity } from 'v5/features/core/enterprise/EnterpriseSidebarIdentity';
import { MenuItem } from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { cn } from 'v5/platform/dom/cn';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import { WithDevOriginService } from 'v5/services/front/DevOriginService/DevOriginService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import {
  WithContentThemeService,
  WithThemeService
} from 'v5/services/front/ThemeService/ThemeService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithNavigationService } from 'v5/services/internal/NavigationService/NavigationService';
import { useCreateEnterpriseWorkspace } from './useCreateEnterpriseWorkspace';

export type EnterpriseNoWorkspaceSidebarProps = WithServices<
  [
    WithAppScopeService,
    WithUserFlowService,
    WithNavigationService<{}>,
    WithThemeService,
    WithContentThemeService,
    WithSupportService,
    WithOnlineStatusService,
    WithDevOriginService,
    WithFeatureService
  ]
> & {
  organizationId: string;
  workspacesRd: RemoteData<EnterpriseWorkspace[]>;
  canManage: boolean;
  className?: string;
};

/**
 * The enterprise main sidebar for an organization with no active workspace, or
 * whose workspace list failed: the organization's menu, the reason, and for
 * managers a way to create the first workspace.
 */
export function EnterpriseNoWorkspaceSidebar({
  services,
  organizationId,
  workspacesRd,
  canManage,
  className
}: EnterpriseNoWorkspaceSidebarProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const handleCreateWorkspace = useCreateEnterpriseWorkspace(
    { services },
    organizationId
  );
  const unavailable = rd.isError(workspacesRd);

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex flex-col gap-2 min-h-(--header-height)">
        <EnterpriseSidebarIdentity
          services={services}
          organizationId={organizationId}
          workspacesRd={workspacesRd}
          canManage={canManage}
        />
        <NavLink to={appRouting.org(organizationId).organization.dashboard()}>
          <MenuItem icon={<Home className="size-4" />}>Dashboard</MenuItem>
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        {unavailable ? (
          <EnterpriseSidebarEmptyState messageKey="workspacesUnavailable" />
        ) : (
          <EnterpriseSidebarEmptyState
            messageKey="emptyWorkspaces"
            withHint={!canManage}
          />
        )}
        {!unavailable && canManage && (
          <Button
            variant="secondary"
            size="sm"
            className="mx-3 self-start"
            onClick={handleCreateWorkspace}
          >
            <Plus className="size-4" />
            {t('newWorkspace')}
          </Button>
        )}
      </SidebarContent>
      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}
